const documentErrorLabels: Record<string, string> = {
  field_selection: 'Output fields', buyer_site_id: 'Buyer site', customer_id: 'Customer',
  carrier_id: 'Carrier', buyer_address: 'Buyer address', consignee_address: 'Consignee address',
  notify_address: 'Notify address', origin_country: 'Origin country', supply_route_id: 'Supply route',
};
/** Keep backend diagnostics intact at every JSON and multipart boundary. */
export function responseObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
export function parseFailure(value: unknown, status: number) {
  const payload = responseObject(value),
    details = responseObject(payload.details);
  const fields: Record<string, string> = {};
  for (const source of [payload.fields, payload.fieldErrors, details.fieldErrors]) {
    for (const [key, messages] of Object.entries(responseObject(source))) {
      fields[key] = Array.isArray(messages)
        ? messages.filter((v) => typeof v === 'string').join(' ')
        : typeof messages === 'string'
          ? messages
          : '';
    }
  }
  // Current document refusals also include issue evidence; retain older field envelopes.
  if (Array.isArray(payload.issues)) {
    for (const value of payload.issues) {
      const issue = responseObject(value);
      if (typeof issue.field === 'string' && typeof issue.message === 'string') {
        const prior = fields[issue.field];
        if (!prior?.includes(issue.message)) fields[issue.field] = [prior, issue.message].filter(Boolean).join(' ');
      }
    }
  }
  const codes = [
    ...new Set(
      [payload.rule_code, ...(Array.isArray(payload.rule_codes) ? payload.rule_codes : [])].filter(
        (v): v is string => typeof v === 'string'
      )
    ),
  ];
  const forms = [payload.formErrors, details.formErrors].flatMap((v) =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []
  );
  const code = typeof payload.error === 'string' ? payload.error : 'REQUEST_FAILED';
  const summary =
    code === 'DOCUMENT_CONTEXT_REQUIRED'
      ? 'Save delivery instructions for this shipment before generating documents. Open Delivery instructions, complete the required details and save, then retry.'
      : code === 'INVENTORY_PLANNING_SETTINGS_CONFLICT'
      ? 'Effective date must be after the most recent Inventory Planning version.'
      : status === 401
        ? 'Your session has ended. Sign in again to continue.'
        : status === 403 && code !== 'ALLOCATION_CANCEL_NOT_PERMITTED'
          ? 'You do not have permission to perform this action.'
          : typeof payload.message === 'string'
            ? payload.message
            : Object.keys(fields).length
              ? 'Review the highlighted fields.'
              : `The request could not be completed (${status}).`;
  const message = [
    summary,
    ...forms,
    ...Object.entries(fields).map(([key, message]) => `${documentErrorLabels[key] ?? key}: ${message}`),
    ...codes.map(
      (code) => `Rule: ${code.replace(/^VAL_/, '').replaceAll('_', ' ').toLowerCase()} (${code})`
    ),
  ].join(' ');
  return { payload, code, fields, message };
}
export const warningsEvent = 'nissei-response-warnings';
export function publishWarnings(value: unknown): void {
  const warnings = responseObject(value).warnings;
  if (Array.isArray(warnings) && warnings.length && typeof window !== 'undefined')
    window.dispatchEvent(new CustomEvent(warningsEvent, { detail: warnings }));
}
